import { type ReactElement, type ReactNode } from 'react'
import { isNotDefined } from '@terros/common'
import { Spotlight } from '@mantine/spotlight'
import { Avatar, Group, Skeleton, Text } from '@mantine/core'
import { type MaterialSymbolType, type QueryResponseWithoutData, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TButton } from '../buttons/button'
import { type DetectedIdResult } from './util'

export type DetectedIdActionProps = {
  detected: DetectedIdResult
  onNavigate: (url: string) => void
}

type Props = DetectedIdActionProps & {
  query: QueryResponseWithoutData
  name: string | undefined
  icon: MaterialSymbolType
  subtitle?: string
  avatarUrl?: string
  leftSection?: ReactNode
}

export const DetectedIdAction = ({
  detected,
  onNavigate,
  query,
  name,
  icon,
  subtitle,
  avatarUrl,
  leftSection,
}: Props): ReactElement => {
  const { t } = useTranslation()
  if (query.error) {
    return (
      <Group p='md'>
        <Text c='red' role='alert'>
          {t('Unable to load this record. It may be unavailable or you may not have access.')}
        </Text>
        <TButton variant='secondary' onClick={() => void query.refetch()}>
          {t('Retry')}
        </TButton>
      </Group>
    )
  }
  if (query.isLoading) {
    return (
      <Group p='md' role='status' aria-label={t('Loading record')}>
        <Skeleton h={36} w={36} radius='md' />
        <Skeleton h={20} w='70%' />
      </Group>
    )
  }
  if (isNotDefined(name)) {
    return (
      <Text p='md' c='dimmed' role='status'>
        {t('Record not found')}
      </Text>
    )
  }
  return (
    <Spotlight.Action
      label={name}
      description={`${t(detected.type)} · ${subtitle ?? detected.id}`}
      leftSection={
        leftSection ?? (
          <Avatar size={36} radius='md' src={avatarUrl}>
            <MaterialSymbolIcon icon={icon} />
          </Avatar>
        )
      }
      onClick={() => onNavigate(detected.url)}
    />
  )
}
